/**
 * PlayerShowcase — Horizontal carousel of player profile cards.
 * Caches PlayerProfileBadge instances by playerId so updates patch in place.
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';
import { PlayerProfileBadge } from '../profile/PlayerProfileBadge';
import type { LobbyPlayer } from '../../../services/LobbyService';

interface PlayerShowcaseEvents {
  'profile:tap': { playerId: string };
  'see_all:tap': void;
}

/** How many badges the rail will render. Older entries fall off when exceeded. */
const MAX_VISIBLE = 20;

export class PlayerShowcase extends EventEmitter<PlayerShowcaseEvents> {
  public element: HTMLElement;
  private rail: HTMLElement;
  private badges: Map<string, PlayerProfileBadge> = new Map();
  private prevIds: Set<string> = new Set();

  constructor() {
    super();
    PlayerShowcase.injectStyles();

    this.element = document.createElement('section');
    this.element.className = 'sdl-pshow';
    this.element.innerHTML = `
      <div class="sdl-pshow-head">
        <div class="sdl-pshow-label">ON THE <span class="sdl-pshow-accent">BLOCK</span></div>
        <button class="sdl-pshow-seeall" type="button">SEE ALL ›</button>
      </div>
      <div class="sdl-pshow-rail" data-rail></div>
    `;

    this.rail = this.element.querySelector('[data-rail]') as HTMLElement;
    const seeAll = this.element.querySelector('.sdl-pshow-seeall') as HTMLElement;
    seeAll.addEventListener('click', () => this.emit('see_all:tap', undefined));
  }

  /**
   * Patch the carousel: reuse existing badges, append new, remove gone.
   * Caps at MAX_VISIBLE — keeps the most recent (assumes input is ordered
   * oldest → newest). Always lands the rail on the rightmost entry so the
   * latest joiner is in view — instant on the first non-empty fill, smooth
   * for subsequent additions so users see the slide.
   */
  setPlayers(players: LobbyPlayer[]): void {
    const visible = players.slice(-MAX_VISIBLE);
    const seen = new Set<string>();
    const fragment = document.createDocumentFragment();

    const isFirstFill = this.prevIds.size === 0 && visible.length > 0;
    let hasNewArrival = false;

    visible.forEach((player) => {
      seen.add(player.id);
      let badge = this.badges.get(player.id);
      if (badge) {
        badge.update(toBadgeData(player));
      } else {
        badge = new PlayerProfileBadge(toBadgeData(player), 'card');
        badge.on('tap', ({ playerId }) => this.emit('profile:tap', { playerId }));
        this.badges.set(player.id, badge);
        if (!isFirstFill) hasNewArrival = true;
      }
      fragment.appendChild(badge.element);
    });

    // Remove gone players (capped-off old entries or genuine departures)
    for (const [id, badge] of this.badges) {
      if (!seen.has(id)) {
        badge.destroy();
        this.badges.delete(id);
      }
    }

    // Append in new order (existing nodes are moved, not duplicated)
    this.rail.appendChild(fragment);
    this.prevIds = seen;

    if (isFirstFill) this.scrollToEnd('instant');
    else if (hasNewArrival) this.scrollToEnd('smooth');
  }

  private scrollToEnd(mode: 'smooth' | 'instant'): void {
    // Wait one frame so layout reflects newly-appended nodes before scrolling.
    requestAnimationFrame(() => {
      if (mode === 'instant') {
        this.rail.scrollLeft = this.rail.scrollWidth;
      } else {
        this.rail.scrollTo({ left: this.rail.scrollWidth, behavior: 'smooth' });
      }
    });
  }

  destroy(): void {
    this.badges.forEach((b) => b.destroy());
    this.badges.clear();
    this.element.remove();
    this.removeAllListeners();
  }

  // ---------- Styles ----------

  private static injectStyles(): void {
    if (document.getElementById('sdl-pshow-styles')) return;
    const style = document.createElement('style');
    style.id = 'sdl-pshow-styles';
    style.textContent = `
      .sdl-pshow { display: block; }
      .sdl-pshow-head {
        display: flex; align-items: center; justify-content: space-between;
        padding: 18px 16px 10px;
      }
      .sdl-pshow-label {
        font-family: var(--font-display);
        font-size: 14px;
        letter-spacing: 3px;
        color: rgba(255,255,255,0.55);
      }
      .sdl-pshow-accent { color: var(--gold); }
      .sdl-pshow-seeall {
        all: unset;
        cursor: pointer;
        font-family: var(--font-body);
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 1.5px;
        color: rgba(255,255,255,0.4);
      }
      .sdl-pshow-seeall:active { color: var(--gold); }
      .sdl-pshow-rail {
        --rail-gap: 12px;
        --visible-cards: 3;
        display: flex;
        gap: var(--rail-gap);
        padding: 4px 16px 8px;
        overflow-x: auto;
        overflow-y: visible;
        scroll-snap-type: x mandatory;
        scroll-padding-inline: 16px;
        -webkit-overflow-scrolling: touch;
      }
      .sdl-pshow-rail::-webkit-scrollbar { height: 0; }
      /* Force exactly N cards visible at any rail width. Overrides the
         default flex-basis of .sdl-pbadge-card (140px) when the card is
         used inside the lobby showcase. */
      .sdl-pshow-rail > .sdl-pbadge-card {
        flex: 0 0 calc(
          (100% - (var(--rail-gap) * (var(--visible-cards) - 1)))
          / var(--visible-cards)
        );
      }
    `;
    document.head.appendChild(style);
  }
}

function toBadgeData(p: LobbyPlayer) {
  return {
    id: p.id,
    username: p.username,
    ringColor: p.ringColor,
    picture: p.picture,
    // No status dot in the lobby — everyone listed here is online by definition.
    division: p.division,
    streak: p.streak,
    streakCold: p.streakCold,
    bank: p.bank,
  };
}
